import type { Json } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type TrackingConfig = {
  ga_measurement_id: string;
  meta_pixel_id: string;
};

export async function getTrackingConfig(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<TrackingConfig> {
  const { data, error } = await client
    .from('integration_settings')
    .select('config')
    .eq('name', 'tracking')
    .single();

  if (error || !data) {
    return { ga_measurement_id: '', meta_pixel_id: '' };
  }

  const config = data.config as Record<string, unknown>;
  return {
    ga_measurement_id: (config.ga_measurement_id as string) ?? '',
    meta_pixel_id: (config.meta_pixel_id as string) ?? '',
  };
}

// Public read of the GA4 / Meta Pixel ids for consent-gated client injection
// (ADR-051). getTrackingConfig reads integration_settings directly and is
// admin-only (anon SELECT revoked); the public site uses this SECDEF RPC instead,
// which exposes ONLY the two ids and is executable by anon + authenticated.
export async function getPublicTrackingConfig(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<TrackingConfig> {
  const { data, error } = await client.rpc('get_public_tracking_config');
  const row = data?.[0];
  if (error || !row) {
    return { ga_measurement_id: '', meta_pixel_id: '' };
  }
  return {
    ga_measurement_id: row.ga_measurement_id ?? '',
    meta_pixel_id: row.meta_pixel_id ?? '',
  };
}

export async function updateTrackingConfig(
  config: TrackingConfig,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  // Audited write path: update_integration_settings is SECURITY DEFINER, guards
  // super_admin (42501 otherwise) and writes an audit_log row. The generated RPC
  // type is { p_name; p_config?; p_enabled? } — omit p_enabled so `enabled` is
  // left untouched (COALESCE in the RPC).
  const { error } = await client.rpc('update_integration_settings', {
    p_name: 'tracking',
    p_config: config as unknown as Json,
  });

  if (error) throw error;
}
